import { RecurrenceType, RepeatInterval } from '../common/shared-dtos-scheduler-common';

export interface ExchangeProtectionModule {
  blackList: BlackListSection;
  quarantine: QuarantineSection;
  antispoofing: AntiSpoofingSection;
  antimalware: ExchangeAntimalwareSection;
  antispam: AntiSpamSection;
  contentControl: ExchangeContentControlSection;
}

export interface BlackListSection {
  enable: boolean;
  list: string[];
}

export interface QuarantineSection {
  deleteFilesOlderThan: number;
}

export interface DomainIpFormValue {
  domain: string;
  ips: string[];
}

export interface DomainIpItem extends DomainIpFormValue {
  ipsCount: number;
  ipsString: string;
}

export interface AntiSpoofingSection {
  enable: boolean;
  domainIpList: DomainIpFormValue[];
}

export interface AntiSpamSection {
  enable: boolean;
  settings: ExchangeAntispamSettings;
  whiteList: ExchangeAntispamWhiteList;
  rules: ExchangeAntispamRule[];
}
export interface ExchangeAntispamRule {
  name: string;
  default: boolean;
  active: boolean;
  scope: ExchangeRuleScope;
  aggressivity: ExchangeAntispamAggressivity;
  filters: ExchangeAntispamFilters;
  checkAuthConnections: boolean;
  actions: ExchangeAntispamRuleActions;
}
export interface UserGroupItem {
  _id?: string;
  name: string;
  type: number;
  specifics: {
    description: string;
    company: {
      id: string;
      name?: string;
    };
    guid: string;
    users: string[];
    domain?: string;
  };
}
export interface UserGroupGridItem extends UserGroupItem {
  emails?: string;
}

export interface CompanyItem {
  companyId: string;
  companyName: string;
}

// exchange.contentControl
export interface ExchangeContentControlSection {
  attachmentFiltering: ExchangeContentControlAttachmentFiltering;
  contentFiltering: ExchangeContentControlContentFiltering;
}
export interface ExchangeContentControlAttachmentFiltering {
  enable: boolean;
  settings: {
    replacementText: string;
  };
  exclusions: ExchangeContentControlFilteringExclusions;
  rules: ExchangeContentControlAttachmentFilteringRule[];
}

// exchange.contentControl.contentFiltering
export interface ExchangeContentControlContentFiltering {
  enable: boolean;
  exclusions: ExchangeContentControlFilteringExclusions;
  rules: ExchangeContentControlFilteringRule[];
}
export interface ExchangeContentControlFilteringExclusions {
  trustedSenders: string[];
  trustedRecipients: string[];
  excludeOnlyIfAllRecipientsAreTrusted: boolean;
}

// exchange.contentControl.attachmentFiltering.rules
export interface ExchangeContentControlAttachmentFilteringRule {
  name: string;
  active: boolean;
  scope: ExchangeRuleScope;
  settings: ExchangeContentControlAttachmentFilteringRuleSettings;
  actions: ExchangeContentControlAttachmentRuleActions;
}

// exchange.contentControl.contentFiltering.rules
export interface ExchangeContentControlFilteringRule {
  name: string;
  active: boolean;
  scope: ExchangeRuleScope;
  settings: ExchangeContentControlContentFilteringRuleSettings;
  actions: ExchangeContentControlRuleActions;
}
export interface ExchangeContentControlContentFilteringRuleSettings {
  filterBySubject: ExchangeContentControlContentFilteringRuleSettingsFilterBy;
  filterByBodyContent: ExchangeContentControlContentFilteringRuleSettingsFilterBy;
}
export interface ExchangeContentControlContentFilteringRuleSettingsFilterBy {
  enabled: boolean;
  expressions: ExchangeContentControlContentFilteringRuleSettingsFilterByExpression[];
}
export interface ExchangeContentControlContentFilteringRuleSettingsFilterByExpression {
  text: string;
  type: ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType;
  matchCase: boolean;
  wholeWord: boolean;
}

export interface ExchangeContentControlAttachmentFilteringRuleSettings {
  detectByContentType: {
    enabled: boolean;
    detectType: ExchangeContentControlAttachmentFilteringRuleSettingsDetectType;
    categoryExecutables: boolean;
    categoryImages: boolean;
    categoryMultimedia: boolean;
    categoryArchives: boolean;
    categorySpreadsheets: boolean;
    categoryPresentations: boolean;
    categoryDocuments: boolean;
    customExtensionsEnabled: boolean;
    customExtensionsList: string[];
    trueFileTypeDetection: boolean;
  };
  detectByFilename: {
    enabled: boolean;
    patterns: string[];
  };
  archiveScan: {
    enabled: boolean;
    depthLimitingEnabled: boolean;
    maxDepth: number;
  };
}

// export interface ExchangeAntispamRuleActions extends ExchangeContentControlAttachmentRuleActions {
//   exchangeSCL: boolean;
// }
export interface ExchangeAntispamRuleActions {
  type: ExchangeAntispamRuleActionType;
  deliverTo: string;
  exchangeSCL: boolean;
  modifySubject: boolean;
  modifySubjectText: string;
  appendHeader: boolean;
  headerName: string;
  headerValue: string;
  saveMailToDisk: boolean;
  saveMailToDiskPath: string;
  archiveToAccount: boolean;
  archiveToAccountAddress: string;
  stopIfMatched: boolean;
  notifyRecipient: boolean;
  notifySender: boolean;
  notifyAccount: boolean;
  notifyAccountRecipients: string;
}

export interface ExchangeAntispamSettings {
  rblDnsAddress: string;
  rblDnsMsTimeout: number;
  rblServers: RblServerItem[];
}
export interface RblServerItem {
  server: string;
  confidenceLevel: number;
}

export interface ExchangeAntispamWhiteList {
  enable: boolean;
  list: string[];
}

export interface ExchangeAntispamFilters {
  asian: boolean;
  cyrillic: boolean;
  fapMaterial: boolean;
  url: boolean;
  rbl: boolean;
  heuristic: boolean;
  cloud: boolean;
}

export enum ExchangeAntispamAggressivity {
  PERMISSIVE = 1,
  NORMAL = 2,
  AGGRESSIVE = 3,
}

export interface ExchangeContentControlAttachmentRuleActions extends ExchangeContentControlRuleActions {
  sendCopyTo: boolean;
  sendCopyToAddress: string;
}
export interface ExchangeContentControlRuleActions {
  type: ExchangeContentControlRuleActionType;
  deliverTo: string;
  modifySubject: boolean;
  modifySubjectText: string;
  appendHeader: boolean;
  headerName: string;
  headerValue: string;
  saveMailToDisk: boolean;
  saveMailToDiskPath: string;
  archiveToAccount: boolean;
  archiveToAccountAddress: string;
  stopIfMatched: boolean;
  notifyRecipient: boolean;
  notifySender: boolean;
  notifyAccount: boolean;
  notifyAccountRecipients: string;
}

export interface ExchangeAntimalwareSection {
  enable: boolean;
  settings: ExchangeAntimalwareSettings;
  rules: ExchangeAntimalwareRule[];
  exclusions: ExchangeAntimalwareExclusionRule[];
  onDemandScanCredentials?: ExchangeAntimalwareOnDemandScanCredentials;
  scanTasks: ExchangeAntimalwareScanTask[];
}

// antimalware.settings
export interface ExchangeAntimalwareSettings {
  appendFooter: boolean;
  footerText: string;
  infectedText: string;
  quarantinedText: string;
  unscannableInfectedText: string;
  unscannableQuarantinedText: string;
}

// antimalware.rules
export interface ExchangeAntimalwareRule {
  name: string;
  active: boolean;
  default: boolean;
  scope: ExchangeRuleScope;
  settings: ExchangeAntimalwareRuleSettings;
  actions: ExchangeAntimalwareRuleActions;
}

// antimalware.exclusions
export interface ExchangeAntimalwareExclusionRule {
  name: string;
  active: boolean;
  scope: ExchangeRuleScope;
}

// antimalware.rules.$.scope, antimalware.exclusions.$.scope, antispam.rules.$.scope
export interface ExchangeRuleScope {
  applyTo: ExchangeRuleScopeApplyToType;
  from: ExchangeRuleScopeEmailSelection;
  to: ExchangeRuleScopeEmailSelection;
  fromSpecific?: string[];
  toSpecific?: string[];
  matchAllRecipients?: boolean;
}

// antimalware.rules.$.settings, antimalware.scanTasks.$.options
export interface ExchangeAntimalwareRuleSettings {
  scanMode: ExchangeAntimalwareRuleSettingsScanMode;
  extensions?: string[];
  maximumSizeEnabled: boolean;
  maximumSize: number;
  maximumDepthEnabled: boolean;
  maximumDepth: number;
  scanPUA: boolean;
}
// antimalware.rules.$.actions, antimalware.scanTasks.$.actions
export interface ExchangeAntimalwareRuleActions {
  infected: ExchangeAntimalwareRuleActionsAction;
  suspected: ExchangeAntimalwareRuleActionsAction;
  unscannable: ExchangeAntimalwareRuleActionsAction;
  stopIfMatched: boolean;
  notifyRecipient?: boolean;
  notifySender?: boolean;
  notifyAccount?: boolean;
  notifyAccountRecipients?: string;
}
interface ExchangeAntimalwareRuleActionsAction {
  action: ExchangeAntimalwareRuleActionType;
  then: ExchangeAntimalwareRuleActionType;
}

// antimalware.scanTasks
export interface ExchangeAntimalwareScanTask {
  taskId: string;
  name: string;
  scanType: ExchangeAntimalwareScanTaskType;
  scheduler: ExchangeAntimalwareScanTaskScheduler;
  target: ExchangeAntimalwareScanTaskTarget;
  options: ExchangeAntimalwareRuleSettings;
  actions: ExchangeAntimalwareRuleActions;
}

// antimalware.scanTasks.$.scheduler
export interface ExchangeAntimalwareScanTaskScheduler {
  startDate: number;
  recurrenceType: RecurrenceType;
  recurrenceStep: number;
  recurrenceStepType: RepeatInterval;

  runEveryMonday: boolean;
  runEveryTuesday: boolean;
  runEveryWednesday: boolean;
  runEveryThursday: boolean;
  runEveryFriday: boolean;
  runEverySaturday: boolean;
  runEverySunday: boolean;

  stopIfLongerEnabled: boolean;
  stopIfLongerStep: number;
  stopIfLongerStepType: RepeatInterval;
}

// antimalware.scanTasks.$.target
export interface ExchangeAntimalwareScanTaskTarget {
  targetType: ExchangeAntimalwareScanTaskTargetType;
  scanEmails: boolean;
  scanContacts: boolean;
  scanTasks: boolean;
  scanAppointments: boolean;
  scanPostItems: boolean;
  scanOnlyUnreadEmails: boolean;
  scanOnlyItemsWithAttachments: boolean;
  scanOnlyRecentReceivedEnabled: boolean;
  scanOnlyRecentReceivedStep: number;
  scanOnlyRecentReceivedStepType: RepeatInterval;
  exclusionsEnabled: boolean;
  exclusions: ExchangeAntimalwareScanTaskTargetExclusion[];
}
export interface ExchangeAntimalwareScanTaskTargetExclusion {
  type: ExchangeAntimalwareScanTaskTargetExclusionType;
  scope: string;
}

// antimalware.scanTasks.$.onDemandScanCredentials
export interface ExchangeAntimalwareOnDemandScanCredentials {
  email: string;
  username: string;
  password: string;
  ewsUrl: string;
}

// enums
export enum ExchangeRuleScopeApplyToType {
  INCOMING = 1,
  INTERNAL = 2,
  OUTGOING = 3,
  ALL = 4,
}

export enum ExchangeAntispamRuleActionType {
  DELIVER = 1,
  QUARANTINE = 2,
  REDIRECT = 3,
  REJECT = 4,
}

export enum ExchangeAntispamActiveState {
  ACTIVE = 1,
  INACTIVE = 0,
}

export enum ExchangeRuleScopeEmailSelection {
  ANY = 1,
  SPECIFIC = 2,
}
export enum ExchangeAntimalwareRuleSettingsScanMode {
  ALL = 1,
  ONLY_APPLICATIONS = 2,
  USER_DEFINED_EXTENSIONS = 3,
  ALL_FILES_EXCEPT_SPECIFIC_EXTENSIONS = 4,
}
export enum ExchangeAntimalwareRuleActionType {
  REPORT_ONLY = 1,
  REMEDIATE = 2,
  DELETE_ATTACHMENT = 3,
  MOVE_TO_QUARANTINE = 4,
  DELETE_EMAIL = 5,
  REPLACE_ATTACHMENT = 6,
}

export enum ExchangeContentControlRuleActionType {
  REPLACE_ATTACHMENT = 1,
  DELETE_ATTACHMENT = 2,
  REJECT_EMAIL = 3,
  MOVE_TO_QUARANTINE = 4,
  REDIRECT = 5,
  DELIVER = 6,
}
export enum ExchangeAntimalwareScanTaskTargetExclusionType {
  EMAIL = 1,
  PUBLIC_FOLDER = 2,
  DATABASE = 3,
}

export enum ExchangeAntimalwareScanTaskTargetType {
  MAILBOXES_AND_PUBLIC_FOLDERS = 1,
  MAILBOXES = 2,
  PUBLIC_FOLDERS = 3,
}
export enum ExchangeAntimalwareScanTaskType {
  CUSTOM = 3,
}

export enum ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType {
  REGULAR_EXPRESSION = 0,
  TEXT = 1,
}

export enum ExchangeContentControlAttachmentFilteringRuleSettingsDetectType {
  ALL_EXCEPT_SELECTED_CATEGORIES = 1,
  ONLY_SELECTED_CATEGORIES = 2,
}
